import { StoreOrderStep } from '@/dealers/_gen/gql'
import AccountMachine from '@/gf/modules/AccountMachine'
import AdditionalCharge from '@/gf/modules/AdditionalCharge'
import Discount from '@/gf/modules/Discount'
import Invoice from '@/gf/modules/Invoice'
import LineItem from '@/gf/modules/LineItem'
import Money from '@/gf/modules/Money'
import Refund from '@/gf/modules/Refund'
import StoreOrderEvent from '@/gf/modules/StoreOrderEvent'
import Time from '@/gf/modules/Time'
import Vendor from '@/gf/modules/Vendor'
import { Pagination, StoreOrderPayload } from '@/types'
import { gql, QueryHookOptions, useQuery } from '@apollo/client'

const query = gql`
  query SearchStoreOrdersNext($page: Int, $filters: [[String]]) {
    storeOrdersSearchLite(page: $page, filters: $filters) {
      pagination {
        totalPages
        totalResults
      }
      storeOrders {
        id
        shortId
        state
        step
        deliveryMethod
        paymentStatus
        updatedAt
        insertedAt
        convenienceFeeRate
        salesTax {
          amount
          currency
        }
        store {
          id
          name
        }
        shippingAmount {
          amount
          currency
        }
        total {
          amount
          currency
        }
        shippingAddress {
          firstName
          lastName
          lineOne
          lineTwo
          city
          state
          country
          postalCode
          companyName
          deliverable
          rdi
        }
        lineItems {
          id
          quantity
          unitPrice {
            amount
            currency
          }
          extendedPrice {
            amount
            currency
          }
        }
        additionalCharges {
          id
          name
          price {
            amount
            currency
          }
          state
        }
        order {
          id
          name
          shortId
          email
          user {
            id
            displayName
            email
            organization {
              name
            }
          }
          requestForQuote {
            fullName
          }
        }
      }
    }
  }
`

type Data = {
  storeOrdersSearchLite: {
    storeOrders: (Omit<StoreOrderPayload, 'order'> & {
      step: StoreOrderStep
      order: Pick<StoreOrderPayload['order'], 'id' | 'name' | 'shortId' | 'email'> & {
        user: Pick<NonNullable<StoreOrderPayload['order']['user']>, 'id' | 'email'> & {
          displayName: string
          organization: { name: string }
        }
        requestForQuote: Pick<
          NonNullable<StoreOrderPayload['order']['requestForQuote']>,
          'fullName'
        >
      }
    })[]
    pagination: Pagination
  }
}

type Vars = {
  page: number
  filters: string[][]
}

const useSearchStoreOrdersNext = (options?: QueryHookOptions<Data, Vars>) => {
  const result = useQuery<Data, Vars>(query, options)

  const data = result.data && {
    ...result.data,
    storeOrdersSearch: {
      ...result.data.storeOrdersSearchLite,
      storeOrders: result.data.storeOrdersSearchLite.storeOrders.map((payload) => ({
        ...payload,
        shippingAmount: payload.shippingAmount && Money.fromPayload(payload.shippingAmount),
        salesTax: payload.salesTax && Money.fromPayload(payload.salesTax),
        lineItems: payload.lineItems && payload.lineItems.map(LineItem.fromPayload),
        additionalCharges:
          payload.additionalCharges && payload.additionalCharges.map(AdditionalCharge.fromPayload),
        refunds: payload.refunds && payload.refunds.map(Refund.fromPayload),
        discounts: payload.discounts && payload.discounts.map(Discount.fromPayload),
        events: payload.events && payload.events.map(StoreOrderEvent.fromPayload),
        insertedAt: Time.fromPayload(payload.insertedAt),
        updatedAt: Time.fromPayload(payload.updatedAt),
        accountMachines:
          payload.accountMachines && payload.accountMachines.map(AccountMachine.fromPayload),
        vendor: payload.vendor && Vendor.fromPayload(payload.vendor),
        invoices:
          payload.invoices && payload.invoices.map((invoice) => Invoice.fromPayload(invoice)),
        total: Money.fromPayload(payload.total),
      })),
    },
  }

  return { ...result, data }
}

export type StoreOrdersSearch = NonNullable<
  ReturnType<typeof useSearchStoreOrdersNext>['data']
>['storeOrdersSearch']

export default useSearchStoreOrdersNext
